// src/components/AiChatbot.tsx
import React, { useEffect, useRef, useState } from "react";
import { useProjectContext } from "../../context/ProjectContext";
import muessigIcon from "../../../src/favicon.ico";
import "./AiChatbot.css";
import { transcribeOnly } from "../../services/aiChatService";
import { runAgent } from "../../services/agentRunnerService";

type ChatMsg = { role: "user" | "assistant"; text: string; ts: number };

interface AiChatbotProps { selectedProject?: string | null; }

const CORPORATE = "#153e8c";
const SESSION_KEY_PREFIX = "aiChatSessionId";

function getOrCreateSessionId(projectNo?: string | null): string {
    const key = `${SESSION_KEY_PREFIX}:${projectNo || "no-project"}`;
    let id = localStorage.getItem(key);
    if (!id) {
        id =
            (globalThis as any)?.crypto?.randomUUID?.() ??
            Math.random().toString(36).slice(2);
        if (id != null) localStorage.setItem(key, id);
    }
    return id as string;
}

/** Entfernt ggf. umschließende Anführungszeichen und trimmt */
function normalizeServerText(x: unknown): string {
    let s = "";
    if (typeof x === "string") s = x;
    else if (x && typeof x === "object" && "toString" in x) s = String(x);
    else return "";
    const t = s.trim();
    if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
        return t.slice(1, -1).trim();
    }
    return t;
}
function escapeHtml(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function parseMaybeJson<T = any>(v: unknown): T | null {
    if (typeof v !== "string") return null;
    const t = v.trim();
    if (!t) return null;
    const looksJson = (t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"));
    if (!looksJson) return null;
    try { return JSON.parse(t) as T; } catch { return null; }
}


/** ---------- File-Erkennung & Öffnen ---------- */
type FilePayload = { type?: string; fileName?: string; mime?: string; base64?: string; dataUrl?: string };

function findFilePayload(raw: any): FilePayload | null {
    if (!raw) return null;

    // Fall A: Top-level direkt das Objekt oder stringifiziertes JSON
    if (typeof raw === "string") {
        const obj = parseMaybeJson<any>(raw);
        if (obj) return findFilePayload(obj);
        return null;
    }
    if (raw.type === "pdf" && (raw.dataUrl || raw.base64)) return raw;

    // Fall B: Bekannte Container-Felder (Objekt oder stringifiziert)
    const containers = [raw.payload, raw.result, raw.output, raw.answer, raw.content, raw.data];

    for (const c of containers) {
        if (!c) continue;

        // B1: Bereits Objekt
        if (typeof c === "object") {
            if (c.type === "pdf" && (c.dataUrl || c.base64)) return c;

            // tiefer verschachtelt: { result: { data: {...} } } oder { result: { payload: {...} } }
            const deep = (c as any)?.data ?? (c as any)?.payload ?? null;
            if (deep && typeof deep === "object" && deep.type === "pdf" && (deep.dataUrl || deep.base64)) {
                return deep;
            }
        }

        // B2: String -> versuchen zu parsen
        if (typeof c === "string") {
            const parsed = parseMaybeJson<any>(c);
            if (parsed) {
                const hit = findFilePayload(parsed);
                if (hit) return hit;
            }
        }
    }

    return null;
}


function ensureDataUrl(fp: FilePayload): string {
    if (fp.dataUrl && fp.dataUrl.startsWith("data:")) return fp.dataUrl;
    const mime = fp.mime || "application/pdf";
    if (fp.base64) return `data:${mime};base64,${fp.base64}`;
    return "";
}

function openFileInNewTab(fp: FilePayload): string | null {
    const dataUrl = ensureDataUrl(fp);
    if (!dataUrl) return null;
    const w = window.open(dataUrl, "_blank", "noopener,noreferrer");
    if (w) return dataUrl;

    // Fallback: programmatic click
    const a = document.createElement("a");
    a.href = dataUrl;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = fp.fileName || "Datei.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return dataUrl;
}


/** Versucht, aus beliebigem Server-Response HTML-fähigen Text zu bauen */
function extractDisplayText(resp: unknown): string {
    try {
        if (typeof resp === "string") {
            const trimmed = resp.trim();
            if (
                (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
                (trimmed.startsWith("[") && trimmed.endsWith("]"))
            ) {
                const obj = JSON.parse(trimmed);
                return extractDisplayText(obj);
            }
            return escapeHtml(normalizeServerText(trimmed)).replace(/\n/g, "<br/>");
        }
        if (resp && typeof resp === "object") {
            const obj: any = resp;

            // ---- NEU: Wenn Datei erkannt, generiere Link-HTML ----
            const fp = findFilePayload(obj);
            if (fp) {
                const dataUrl = ensureDataUrl(fp);
                const name = fp.fileName || "Datei.pdf";
                if (dataUrl) {
                    const safeName = escapeHtml(name);
                    const safeUrl = escapeHtml(dataUrl);
                    return `📄 PDF geöffnet.`;
                }
            }

// häufige Felder (Text)
            const candidates = [obj.answer, obj.output, obj.message, obj.content, obj.text, obj.result];

// NEU: zuerst prüfen, ob in diesen Feldern ein (stringifizierter) PDF-Payload steckt
            for (const v of candidates) {
                if (v == null) continue;

                // direkter Objekt-Hit
                if (typeof v === "object") {
                    const fp2 = findFilePayload(v);
                    if (fp2) {
                        const dataUrl2 = ensureDataUrl(fp2);
                        const name2 = fp2.fileName || "Datei.pdf";
                        if (dataUrl2) {
                            const safeName2 = escapeHtml(name2);
                            const safeUrl2 = escapeHtml(dataUrl2);
                            return `📄 Datei wird angezeigt. <a class="fileLink" href="${safeUrl2}" target="_blank" rel="noopener noreferrer">Nochmal öffnen / Download (${safeName2})</a>`;
                        }
                    }
                }

                // stringifiziert -> parsen
                if (typeof v === "string") {
                    const parsed = parseMaybeJson<any>(v);
                    if (parsed) {
                        const fp2 = findFilePayload(parsed);
                        if (fp2) {
                            const dataUrl2 = ensureDataUrl(fp2);
                            const name2 = fp2.fileName || "Datei.pdf";
                            if (dataUrl2) {
                                const safeName2 = escapeHtml(name2);
                                const safeUrl2 = escapeHtml(dataUrl2);
                                return `📄 Datei wird angezeigt. <a class="fileLink" href="${safeUrl2}" target="_blank" rel="noopener noreferrer">Nochmal öffnen / Download (${safeName2})</a>`;
                            }
                        }
                    }
                }
            }

            // Fallback: erstes Stringfeld normal anzeigen
            const firstString = candidates.find((v) => typeof v === "string");
            if (firstString) {
                return escapeHtml(normalizeServerText(firstString as string)).replace(/\n/g, "<br/>");
            }


            // nichts Passendes: pretty JSON
            const json = JSON.stringify(obj, null, 2);
            const safe = escapeHtml(json);
            return `<pre class="json">${safe}</pre>`;
        }
        return "";
    } catch {
        return escapeHtml(String(resp ?? ""));
    }
}

/** ---------- Komponente ---------- */
const AiChatbot: React.FC<AiChatbotProps> = ({ selectedProject }) => {
    const { selectedProject: ctxProject } = useProjectContext();
    const activeProject = (selectedProject ?? ctxProject ?? "")?.trim() || undefined;

    const [open, setOpen] = useState(true);
    const [messages, setMessages] = useState<ChatMsg[]>([
        {
            role: "assistant",
            text:
                "Willkommen! Schreibe deine Frage oben in das Feld oder nutze das Mikro. Du kannst den transkribierten Text danach noch bearbeiten.",
            ts: Date.now(),
        },
    ]);

    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);

    // Audio
    const [recording, setRecording] = useState(false);
    const [recTime, setRecTime] = useState(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<BlobPart[]>([]);
    const timerRef = useRef<number | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const chatRef = useRef<HTMLDivElement>(null);
    const sessionIdRef = useRef<string>(getOrCreateSessionId(activeProject));

    useEffect(() => { sessionIdRef.current = getOrCreateSessionId(activeProject); }, [activeProject]);
    useEffect(() => { if (chatRef.current) chatRef.current.scrollTo({ top: 0, behavior: "auto" }); }, [messages.length]);

    const push = (m: ChatMsg) => setMessages((prev) => [...prev, m]);

    const guardNoProject = (): boolean => {
        if (!activeProject) {
            push({ role: "assistant", text: "Bitte zuerst ein Projekt auswählen.", ts: Date.now() });
            return true;
        }
        return false;
    };

    const sendText = async () => {
        const text = input.trim();
        if (!text) return;
        if (guardNoProject()) return;

        const userMsg: ChatMsg = { role: "user", text, ts: Date.now() };
        setMessages((m) => [...m, userMsg]);
        setInput("");
        setSending(true);

        try {
            const resp = await runAgent({
                sessionId: sessionIdRef.current,
                projectNo: activeProject,
                message: text,
            });

            // --- HARTE WEICHE FÜR PDF-PAYLOAD (Server liefert { result, payload, conversation_id }) ---
            const payload = (resp && typeof resp === "object") ? (resp as any).payload ?? null : null;

            if (payload && payload.type === "pdf" && (payload.dataUrl || payload.base64)) {
                // PDF inline im neuen Tab anzeigen (ohne Auto-Download)
                const inlinedUrl = openPdfInlineInNewTab(payload as FilePayload);
                const name = payload.fileName || "Datei.pdf";
                const dataUrl = inlinedUrl || ensureDataUrl(payload as FilePayload);
                const html = `📄 PDF angezeigt.`;
                push({ role: "assistant", text: html, ts: Date.now() });
                return; // <<– nichts weiter rendern
            }


            // --- Kein PDF? -> wie gehabt Text/JSON hübsch anzeigen ---
            const display = extractDisplayText(resp) || "Keine Antwort erhalten.";
            push({ role: "assistant", text: display, ts: Date.now() });

        } catch (e: any) {
            const isCors = e?.message?.toString?.().includes("Network Error");
            push({
                role: "assistant",
                text: isCors
                    ? "Agent-Runner nicht erreichbar (CORS/Netzwerk). Bitte CORS am Server aktivieren."
                    : "Fehler beim Agent-Runner.",
                ts: Date.now(),
            });
        } finally {
            setSending(false);
        }
    };


    /** === Audio: Start/Stop/Toggle (unverändert) === */
    const startRecording = async () => {
        if (guardNoProject()) return;
        if (recording) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");

            const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            mediaRecorderRef.current = mr;
            chunksRef.current = [];

            mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
            mr.onstop = async () => {
                if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
                setRecording(false);
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                setRecTime(0);

                try { stream.getTracks().forEach((t) => t.stop()); } catch {}
                streamRef.current = null;

                try {
                    const raw = await transcribeOnly(blob, navigator.language?.slice(0, 2) || "de");
                    const t = normalizeServerText(raw);
                    if (t) setInput((prev) => (prev ? `${prev} ${t}` : t));
                    else push({ role: "assistant", text: "Konnte nichts transkribieren.", ts: Date.now() });
                } catch {
                    push({ role: "assistant", text: "Transkription fehlgeschlagen.", ts: Date.now() });
                }
            };

            mr.start(200);
            setRecording(true);
            setRecTime(0);
            timerRef.current = window.setInterval(() => setRecTime((x) => x + 1), 1000);
        } catch {
            push({ role: "assistant", text: "Mikrofonzugriff verweigert oder nicht verfügbar.", ts: Date.now() });
        }
    };
    const stopRecording = () => {
        if (!recording) return;
        try { mediaRecorderRef.current?.state === "recording" && mediaRecorderRef.current.stop(); } catch {}
    };
    const toggleRecording = async () => { if (recording) stopRecording(); else await startRecording(); };

    const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!sending) sendText(); }
    };

    // Spinner
    const Spinner = ({ size = 20, stroke = 3 }: { size?: number; stroke?: number }) => (
        <svg className="ai-spinner" width={size} height={size} viewBox="0 0 50 50" aria-hidden="true" focusable="false">
            <circle className="ai-spinner__track" cx="25" cy="25" r="20" fill="none" strokeWidth={stroke} />
            <circle className="ai-spinner__arc" cx="25" cy="25" r="20" fill="none" strokeWidth={stroke} strokeLinecap="round" />
        </svg>
    );

    const PersonIcon = ({ size = 28 }: { size?: number }) => (
        <svg viewBox="0 0 24 24" aria-hidden="true" width={size} height={size}>
            <circle cx="12" cy="12" r="11" fill="var(--corp, #153e8c)" opacity="0.12" />
            <path d="M12 12a4 4 0 1 0-0.001-8.001A4 4 0 0 0 12 12Zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5Z" fill="var(--corp, #153e8c)"/>
        </svg>
    );

    function normalizeBase64(b64: string): string {
        // URL-safe -> standard
        return b64.replace(/-/g, "+").replace(/_/g, "/");
    }

    function base64ToUint8ArraySafe(b64: string): Uint8Array {
        const s = normalizeBase64(b64);
        // fehlende Padding '=' ergänzen
        const pad = s.length % 4;
        const padded = pad ? s + "=".repeat(4 - pad) : s;
        const bin = atob(padded);
        const len = bin.length;
        const out = new Uint8Array(len);
        for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
        return out;
    }

    function openPdfInlineInNewTab(fp: FilePayload): string | null {
        const mime = fp.mime || "application/pdf";
        const b64 = fp.base64 || (fp.dataUrl?.split(",")[1] ?? "");
        if (!b64) return null;

        // 1) Blob-URL bauen
        let bytes: Uint8Array;
        try {
            bytes = base64ToUint8ArraySafe(b64);
        } catch {
            const du = fp.dataUrl && fp.dataUrl.startsWith("data:") ? fp.dataUrl : "";
            if (du) {
                const w1 = window.open(du, "_blank"); // kein 'noreferrer'!
                if (w1) return du;
            }
            return null;
        }

        const blob = uint8ToBlob(bytes, mime);
        const blobUrl = URL.createObjectURL(blob);

        // 2) Versuch A: direkt Browser-PDF-Viewer mit Blob-URL
        const wA = window.open(blobUrl, "_blank"); // bewusst ohne 'noreferrer'
        if (wA) return blobUrl;

        // 3) Fallback: about:blank öffnen (same-origin) und HTML mit <embed> schreiben
        const wB = window.open("", "_blank", "noopener");
        if (wB && wB.document) {
            const name = (fp.fileName || "Datei.pdf").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            wB.document.write(`<!doctype html>
<html lang="de"><head>
<meta charset="utf-8"><title>${name}</title>
<style>html,body,embed{height:100%;width:100%;margin:0;border:0;padding:0;background:#111;}</style>
</head><body>
  <embed src="${blobUrl}" type="${mime}" />
</body></html>`);
            wB.document.close();
            wB.addEventListener("beforeunload", () => URL.revokeObjectURL(blobUrl));
            return blobUrl;
        }

        const a = document.createElement("a");
        a.href = blobUrl;
        a.target = "_blank";
        a.rel = "noopener";
        a.click();
        return blobUrl;
    }

    function uint8ToBlob(bytes: Uint8Array, mime = "application/pdf"): Blob {
        // Kopie erzeugen → garantiertes ArrayBuffer (kein SharedArrayBuffer / ArrayBufferLike)
        const copy = new Uint8Array(bytes.byteLength);
        copy.set(bytes);
        return new Blob([copy.buffer as ArrayBuffer], { type: mime });
    }


    return (
        <div className="AI-Chatbot-CSS page" style={{ ["--corp" as any]: CORPORATE } as React.CSSProperties}>
            <div className="header">
                <button title="AI Chatbot" onClick={() => setOpen((o) => !o)} className="avatarBtn">
                    <img src={muessigIcon} alt="Chatbot" className="brandIcon" />
                    <span className="brandTitle">Chatbot (Beta)</span>
                </button>
                {activeProject && (
                    <div className="projectWrap">
                        <span className="projectLabel">Projekt: <strong>{activeProject}</strong></span>
                    </div>
                )}
            </div>

            {open && (
                <>
                    {activeProject ? (
                        <div className="panel">
                            {/* Composer oben */}
                            <div className="composer">
                <textarea
                    className="composer__input"
                    placeholder="Nachricht eingeben … (Shift+Enter für Zeilenumbruch)"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    disabled={sending}
                    rows={2}
                />
                                <button
                                    className={`micInlineBtn ${recording ? "recording" : ""}`}
                                    title={recording ? "Klicken zum Beenden" : "Klicken zum Starten"}
                                    onClick={toggleRecording}
                                    disabled={sending}
                                >
                                    <span className="micGlyph">🎤</span>
                                    <span className="micTimer">{recording ? `${recTime}s` : ""}</span>
                                    {recording && <span className="micPulse" aria-hidden="true" />}
                                </button>
                                <button className="composer__send" onClick={sendText} disabled={sending || !input.trim()} title="Senden">
                                    Senden
                                </button>
                            </div>

                            {/* Antworten-Container (neueste oben) */}
                            <div ref={chatRef} className="chatList">
                                {sending && (
                                    <div className="msg msg--assistant thinkingRow">
                                        <div className="msg__avatar"><img src={muessigIcon} alt="Chatbot" className="thinkingIcon" /></div>
                                        <div className="msg__bubble typingBubble" role="status" aria-live="polite" aria-label="Antwort wird erstellt">
                                            <Spinner /><span className="sr-only">Antwort wird erstellt…</span>
                                        </div>
                                    </div>
                                )}
                                {messages.slice().reverse().map((m, i) => {
                                    const isUser = m.role === "user";
                                    const roleClass = isUser ? "msg--user" : "msg--assistant";
                                    return (
                                        <div key={`${m.ts}-${i}`} className={`msg ${roleClass}`}>
                                            <div className="msg__avatar">
                                                {isUser ? <PersonIcon size={28} /> : <img src={muessigIcon} alt="Chatbot" className="assistantIcon" />}
                                            </div>
                                            <div className="msg__bubble" dangerouslySetInnerHTML={{ __html: m.text }} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        <div className="emptyWrap">
                            <img src={muessigIcon} alt="Muessig" className="emptyIcon" />
                            <div className="emptyTitle">Bitte ein Projekt auswählen</div>
                            <div className="emptyHint">Der Muessig-Chatbot ist nur aktiv, wenn ein Projekt ausgewählt ist.</div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AiChatbot;